import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Button, Skeleton } from '@mui/material';
import { LockClientSVG, PaymentLinksPositionSVG, UnlockSVG } from '../../../components';

import PaymentLinksPositionSection from './forms/sections/PaymentLinksPositionSection';
import { useTranslation } from 'react-i18next';
import { getColor } from '../../../helpers/getColor';
import BlockAndUnblockDialog from './dialogs/BlockAndUnblockDialog';
import { useClientUIContext } from './context/ClientByIdUIContext';
import { checkPermissions } from '../../../helpers/PermissionsChecker';
import { useParams } from 'react-router-dom';
import { ProductService, usersService } from '../../../services';
import CustomAlert from '../../../components/Alert';

const PRODUCT_STATUS = {
    BLOCKED: 'blocked',
    UNBLOCKED: 'unblocked'
};

const PRODUCT_TYPES = {
    PAYMENT_LINK: 18
};

export default function ClientPaymentLinksPositionById () {
    const { t } = useTranslation();
    // Alert states

    const [openAlert, setOpenAlert] = useState(false);
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });
    const { id } = useParams();
    const clientId = parseInt(id);

    const [isLoading, setIsLoading] = useState(false);
    const [reload, setReload] = useState(false);

    const [userId, setUserId] = useState(null);
    const [userStatus, setUserStatus] = useState(null);
    const [showBlockUserDialog, setShowBlockUserDialog] = useState(false);

    // Client UI Context
    const clientUIContext = useClientUIContext();
    const clientUIProps = useMemo(() => {
        return {
            clientTabData: clientUIContext.clientTabData,
        };
    }, [clientUIContext]);
    const formattedName = clientUIProps?.clientTabData?.formattedName;

    const statusText = userStatus === PRODUCT_STATUS.BLOCKED
        ? t('paymentLinksPosition.table.generation.blocked')
        : t('paymentLinksPosition.table.generation.enabled');

    useEffect(() => {
        const storedUser = localStorage.getItem('sessionUser');
        const parsed = storedUser && JSON.parse(storedUser);
        if (parsed?.id) setUserId(parsed.id);
    }, []);

    useEffect(() => {
        const fetchClientData = async () => {
            try {
                const response = await usersService.getUserWalletHome(clientId);
                const product = response?.products?.find(p => p.type === PRODUCT_TYPES.PAYMENT_LINK);

                if (product?.id_status === 1) {
                    setUserStatus(PRODUCT_STATUS.UNBLOCKED);
                }
                else if (product?.id_status === 2) {
                    setUserStatus(PRODUCT_STATUS.BLOCKED);
                }
                else {
                    setUserStatus(null);
                }
            }
            catch (error) {
                setAlert({
                    isOpen: true,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.generalDescription'),
                    className: 'clientsMC_Alert--error'
                });
            }
        };

        if (clientId) fetchClientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, reload]);

    const handleChangeStatus = async () => {
        setIsLoading(true);

        try {
            const date = new Date().toISOString();
            const body = {
                id_user_provider: clientId,
                id_user_bo: userId,
                date
            };

            const response = await ProductService.changeStatus(body);

            if (response?.id_product_status === 1) {
                setUserStatus(PRODUCT_STATUS.UNBLOCKED);
            }
            else if (response?.id_product_status === 2) {
                setUserStatus(PRODUCT_STATUS.BLOCKED);
            }

            setAlert({
                isOpen: true,
                severity: 'success',
                description: t('details.alert.blockPaymentLink'),
                className: 'success'
            });

            setReload(prev => !prev);
        }
        catch (error) {
            setAlert({
                isOpen: true,
                severity: 'error',
                description: t('details.alert.errorBlockPaymentLink'),
                className: 'error'
            });
        }
        finally {
            setIsLoading(false);
            setShowBlockUserDialog(false);
        }
    };

    const renderStatusButton = () => {
        const isBlocked = userStatus === PRODUCT_STATUS.BLOCKED;
        return (
            <Button
                className={`buttonAction ${isBlocked ? 'button-action--unblock' : 'button-action--block'}`}
                onClick={() => setShowBlockUserDialog(true)}
                endIcon={isBlocked ? <UnlockSVG /> : <LockClientSVG color="white" />}
            >
                {t(isBlocked ? 'buttons.unblock' : 'buttons.block')}
            </Button>
        );
    };

    return (
        <Box style={{ backgroundColor: '#FFF', boxShadow: '0px 0px 30px 0px rgba(82, 63, 105, 0.06)' }} width={'100%'} marginTop={'1rem'} padding={'1rem'} >
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />
            <Box
                gap={'1rem'}>
                <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'>
                    <Box display='flex' gap={'8px'} alignItems='center'>
                        {<PaymentLinksPositionSVG customStroke={getColor('primary', 700)} />}
                        <Typography variant='h5'>{t('paymentLinksPosition.title')}</Typography>
                        <Typography className="payment-link__status-text">
                            {userStatus === null ? (
                                <Skeleton variant="text" width={140} />
                            ) : (
                                <>
                                    {t('paymentLinksPosition.table.generation.text')} {statusText}
                                </>
                            )}
                        </Typography>
                        {checkPermissions('paymentLinks.link_block_button') && userStatus !== null && renderStatusButton()}
                    </Box>
                </Box>
                <Box>
                    <PaymentLinksPositionSection
                        setAlert={setAlert} />
                </Box>

            </Box>
            <BlockAndUnblockDialog
                open={showBlockUserDialog}
                onClose={() => setShowBlockUserDialog(false)}
                onConfirm={handleChangeStatus}
                action={userStatus === PRODUCT_STATUS.BLOCKED ? 'unblock' : 'block'}
                userName={formattedName}
                isLoading={isLoading}
            />

        </Box>
    );
}
